import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import GetAppIcon from '@mui/icons-material/GetApp';
import DeleteIcon from '@mui/icons-material/Delete';
import { dsgvoAPI } from '../services/dsgvoApi';
import { useToast } from '../context/ToastContext';

interface DataManagementModalProps {
  open: boolean;
  onClose: () => void;
  onDeletionRequested?: () => void;
}

const DataManagementModal: React.FC<DataManagementModalProps> = ({
  open,
  onClose,
  onDeletionRequested,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [exportFormat, setExportFormat] = useState<'json' | 'csv'>('json');
  const [understood, setUnderstood] = useState(false);
  const [activeTab, setActiveTab] = useState<'export' | 'delete'>('export');
  const { showSuccess, showError } = useToast();

  const handleExportData = async () => {
    try {
      setIsLoading(true);
      const response = await dsgvoAPI.exportData();
      const data = response.data;

      if (exportFormat === 'csv') {
        downloadCSV(data);
      } else {
        downloadJSON(data);
      }

      showSuccess('Datenexport erfolgreich gestartet');
    } catch (err: any) {
      const message = err.response?.data?.message || 'Fehler beim Datenexport';
      showError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadJSON = (data: any) => {
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dsgvo-export-${new Date().toISOString().slice(0, 10)}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadCSV = (data: any) => {
    const flatData = flattenObject(data);
    const headers = Object.keys(flatData[0] || {});
    const csv = [
      headers.join(','),
      ...flatData.map((row: any) =>
        headers
          .map((h) => {
            const value = row[h];
            if (typeof value === 'string' && value.includes(',')) {
              return `"${value}"`;
            }
            return value || '';
          })
          .join(',')
      ),
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dsgvo-export-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const flattenObject = (obj: any, prefix = ''): any => {
    if (Array.isArray(obj)) {
      return obj.map((item, idx) => flattenObject(item, `${prefix}[${idx}]`));
    }
    if (obj !== null && typeof obj === 'object') {
      return [
        Object.keys(obj).reduce((acc: any, key) => {
          const value = obj[key];
          const newKey = prefix ? `${prefix}.${key}` : key;
          if (value !== null && typeof value === 'object') {
            return { ...acc, ...flattenObject(value, newKey) };
          }
          acc[newKey] = value;
          return acc;
        }, {}),
      ];
    }
    return [{ [prefix]: obj }];
  };

  const handleDeleteData = async () => {
    if (!understood) {
      showError('Bitte bestätigen Sie, dass Sie die Konsequenzen verstanden haben');
      return;
    }

    try {
      setIsLoading(true);
      await dsgvoAPI.deleteData();
      showSuccess(
        'Löschungsanfrage erfolgreich eingereicht. Ihre Daten werden in 30 Tagen gelöscht.'
      );
      onDeletionRequested?.();
      onClose();
    } catch (err: any) {
      const message = err.response?.data?.message || 'Fehler beim Löschen der Daten';
      showError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Datenverwaltung & DSGVO-Rechte</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <Button
              variant={activeTab === 'export' ? 'contained' : 'outlined'}
              onClick={() => setActiveTab('export')}
              startIcon={<GetAppIcon />}
            >
              Daten exportieren
            </Button>
            <Button
              variant={activeTab === 'delete' ? 'contained' : 'outlined'}
              color={activeTab === 'delete' ? 'error' : 'inherit'}
              onClick={() => setActiveTab('delete')}
              startIcon={<DeleteIcon />}
            >
              Daten löschen
            </Button>
          </Box>

          {activeTab === 'export' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="textSecondary">
                Laden Sie alle Ihre persönlichen Daten herunter, die in unserem
                System gespeichert sind.
              </Typography>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant={exportFormat === 'json' ? 'contained' : 'outlined'}
                  onClick={() => setExportFormat('json')}
                  size="small"
                >
                  JSON
                </Button>
                <Button
                  variant={exportFormat === 'csv' ? 'contained' : 'outlined'}
                  onClick={() => setExportFormat('csv')}
                  size="small"
                >
                  CSV
                </Button>
              </Box>
            </Box>
          )}

          {activeTab === 'delete' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Alert severity="warning">
                <strong>Warnung:</strong> Dies kann nicht rückgängig gemacht werden!
              </Alert>

              <Typography variant="body2">
                Die Löschung Ihrer Daten wird in 30 Tagen wirksam. Sie können diese
                Anfrage in dieser Zeit stornieren.
              </Typography>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={understood}
                    onChange={(e) => setUnderstood(e.target.checked)}
                  />
                }
                label="Ich verstehe, dass meine Daten in 30 Tagen unwiederbringlich gelöscht werden."
              />
            </Box>
          )}

          {isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress />
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Abbrechen
        </Button>
        <Button
          onClick={activeTab === 'export' ? handleExportData : handleDeleteData}
          variant="contained"
          color={activeTab === 'delete' ? 'error' : 'primary'}
          disabled={isLoading || (activeTab === 'delete' && !understood)}
        >
          {isLoading ? 'Wird verarbeitet...' : activeTab === 'export' ? 'Exportieren' : 'Löschen anfordern'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DataManagementModal;
